var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import axios from 'axios';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FertilizerLegend from './FertilizerLegend';
import FertilizerPanelSummary from './FertilizerPanelSummary';
import { assessmentDateError, finiteNumber, formatCalendarDate, planningDateError, taskMapsAvailable } from './taskMapData';
// Old formulas and NDVI calculations are never substitutes for this explicit pilot.
export function latestSoilNitrogenCalculation(rows, fieldId, season) {
    var _a;
    return (_a = rows.filter((row) => {
        var _a, _b, _c;
        return row.fertilizer_field_id === fieldId && row.fertilizer_season === season
            && ((_a = row.fertilizer_source_metadata) === null || _a === void 0 ? void 0 : _a.calculation_scope) === 'soil_nitrogen'
            && ((_c = (_b = row.fertilizer_source_metadata) === null || _b === void 0 ? void 0 : _b.soil_nitrogen_prescription) === null || _c === void 0 ? void 0 : _c.method) === 'chern_enok_soil';
    })
        .slice().sort((a, b) => {
        var _a, _b;
        return (Date.parse((_a = b.fertilizer_date_created) !== null && _a !== void 0 ? _a : '') || 0) - (Date.parse((_b = a.fertilizer_date_created) !== null && _b !== void 0 ? _b : '') || 0)
            || b.id - a.id;
    })[0]) !== null && _a !== void 0 ? _a : null;
}
export function soilNitrogenCanCreate(preview, accepted, dateIssue, canCreate) {
    return Boolean(canCreate && !dateIssue && accepted && (preview === null || preview === void 0 ? void 0 : preview.ready) && preview.source_selection_key);
}
export function soilNitrogenUsesNitrogenOnly(prescription) {
    var _a;
    return Boolean(prescription && ((_a = prescription.version) !== null && _a !== void 0 ? _a : 0) >= 2
        && prescription.variant === 'nitrogen_only' && prescription.phosphorus_used === false);
}
const PILOT_NOTICE = 'Предварительный расчёт — проверьте перед внесением.';
function serverMessage(error, fallback) {
    var _a, _b;
    if (axios.isAxiosError(error)) {
        const detail = (_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.detail;
        if (typeof detail === 'string')
            return detail;
    }
    return fallback;
}
export default function SoilNitrogenPanel(props) {
    // A context change unmounts all requests and prevents even a one-frame stale map.
    return React.createElement(SoilNitrogenContent, Object.assign({ key: JSON.stringify([props.field.id, props.season, props.token]) }, props));
}
function SoilNitrogenContent({ token, field, season, planningContext, onMapUrlChange, onCollapse, onClose, canCreate }) {
    var _a, _b, _c, _d, _e, _f, _g;
    const [records, setRecords] = React.useState([]);
    const [loadingRecords, setLoadingRecords] = React.useState(true);
    const [recordsError, setRecordsError] = React.useState(null);
    const [recordsVersion, setRecordsVersion] = React.useState(0);
    const [editing, setEditing] = React.useState(false);
    const [assessmentDate, setAssessmentDate] = React.useState(planningContext.today);
    const [plannedDate, setPlannedDate] = React.useState(planningContext.today);
    const [preview, setPreview] = React.useState(null);
    const [accepted, setAccepted] = React.useState(false);
    const [previewing, setPreviewing] = React.useState(false);
    const [creating, setCreating] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [showSoil, setShowSoil] = React.useState(false);
    const [chart, setChart] = React.useState(null);
    const [detail, setDetail] = React.useState(null);
    const [loadingChart, setLoadingChart] = React.useState(false);
    const [chartError, setChartError] = React.useState(null);
    const [chartVersion, setChartVersion] = React.useState(0);
    const previewRequest = React.useRef(null);
    const creationRequest = React.useRef(null);
    const mapCallback = React.useRef(onMapUrlChange);
    mapCallback.current = onMapUrlChange;
    const current = latestSoilNitrogenCalculation(records, field.id, season);
    const active = taskMapsAvailable(planningContext, season);
    const assessmentIssue = assessmentDateError(assessmentDate, planningContext, season);
    const dateIssue = planningDateError(assessmentDate, plannedDate, planningContext, season);
    const formVisible = editing || !current;
    const band = showSoil ? 1 : 5;
    const chartKey = `${(_a = current === null || current === void 0 ? void 0 : current.id) !== null && _a !== void 0 ? _a : ''}:${band}`;
    const visibleChart = (chart === null || chart === void 0 ? void 0 : chart.key) === chartKey ? chart.data : null;
    const visibleRecord = (detail === null || detail === void 0 ? void 0 : detail.id) === (current === null || current === void 0 ? void 0 : current.id) ? detail : current;
    const metadata = visibleRecord === null || visibleRecord === void 0 ? void 0 : visibleRecord.fertilizer_source_metadata;
    const prescription = metadata === null || metadata === void 0 ? void 0 : metadata.soil_nitrogen_prescription;
    const nitrogenOnly = soilNitrogenUsesNitrogenOnly(prescription);
    const legacySaved = Boolean(current && !nitrogenOnly);
    React.useEffect(() => () => {
        var _a, _b;
        (_a = previewRequest.current) === null || _a === void 0 ? void 0 : _a.abort();
        (_b = creationRequest.current) === null || _b === void 0 ? void 0 : _b.abort();
        mapCallback.current(null);
    }, []);
    React.useEffect(() => {
        const controller = new AbortController();
        setLoadingRecords(true);
        setRecordsError(null);
        mapCallback.current(null);
        if (!token) {
            setRecords([]);
            setRecordsError('Для просмотра расчётов войдите в систему.');
            setLoadingRecords(false);
            return () => controller.abort();
        }
        axios.get('/fertilizer/list', { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
            .then((response) => {
            if (!controller.signal.aborted)
                setRecords(Array.isArray(response.data) ? response.data : []);
        }).catch((requestError) => {
            if (!controller.signal.aborted)
                setRecordsError(serverMessage(requestError, 'Не удалось загрузить расчёты.'));
        }).finally(() => { if (!controller.signal.aborted)
            setLoadingRecords(false); });
        return () => controller.abort();
    }, [token, recordsVersion]);
    React.useEffect(() => {
        var _a, _b;
        (_a = previewRequest.current) === null || _a === void 0 ? void 0 : _a.abort();
        (_b = creationRequest.current) === null || _b === void 0 ? void 0 : _b.abort();
        setPreview(null);
        setAccepted(false);
        setPreviewing(false);
        setCreating(false);
        setError(null);
    }, [assessmentDate, plannedDate, planningContext.today, planningContext.can_plan, canCreate]);
    React.useEffect(() => {
        const controller = new AbortController();
        mapCallback.current(null);
        setChart(null);
        setChartError(null);
        if (!current || formVisible || !active || loadingRecords) {
            setLoadingChart(false);
            return () => controller.abort();
        }
        setLoadingChart(true);
        const config = { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } };
        Promise.all([
            axios.get(`/fertilizer/detail/${current.id}`, config),
            axios.get(`/fertilizer/detail/chart/${current.id}/${band}`, config),
        ]).then(([detailResponse, chartResponse]) => {
            if (controller.signal.aborted)
                return;
            const data = chartResponse.data;
            if (!data || !Array.isArray(data.items) || !finiteNumber(data.area))
                throw new Error('Invalid chart');
            setDetail(detailResponse.data);
            setChart({ key: chartKey, data });
            mapCallback.current(`/fertilizer/map/${band}/false/${current.id}`);
        }).catch((requestError) => {
            if (!controller.signal.aborted)
                setChartError(serverMessage(requestError, 'Не удалось загрузить карту и легенду. Повторите загрузку.'));
        }).finally(() => { if (!controller.signal.aborted)
            setLoadingChart(false); });
        return () => controller.abort();
    }, [current === null || current === void 0 ? void 0 : current.id, band, chartKey, formVisible, token, active, loadingRecords, chartVersion]);
    const changeDate = (value, planned) => {
        var _a, _b;
        // Abort synchronously, before the next render/effect can apply a stale answer.
        (_a = previewRequest.current) === null || _a === void 0 ? void 0 : _a.abort();
        (_b = creationRequest.current) === null || _b === void 0 ? void 0 : _b.abort();
        setPreview(null);
        setAccepted(false);
        setError(null);
        if (planned)
            setPlannedDate(value);
        else
            setAssessmentDate(value);
    };
    const requestPreview = () => __awaiter(this, void 0, void 0, function* () {
        var _h, _j;
        if (dateIssue || !canCreate || !token || creating)
            return;
        (_h = previewRequest.current) === null || _h === void 0 ? void 0 : _h.abort();
        const controller = new AbortController();
        previewRequest.current = controller;
        setPreviewing(true);
        setPreview(null);
        setAccepted(false);
        setError(null);
        try {
            const response = yield axios.post('/fertilizer/soil-nitrogen/preview', {
                field: field.id, season, assessment_date: assessmentDate,
            }, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } });
            if (controller.signal.aborted)
                return;
            if (typeof ((_j = response.data) === null || _j === void 0 ? void 0 : _j.ready) !== 'boolean')
                throw new Error('Invalid preview');
            setPreview(response.data);
        }
        catch (requestError) {
            if (!controller.signal.aborted)
                setError(serverMessage(requestError, 'Не удалось проверить почвенные анализы. Повторите попытку.'));
        }
        finally {
            if (!controller.signal.aborted)
                setPreviewing(false);
        }
    });
    const createCalculation = () => __awaiter(this, void 0, void 0, function* () {
        var _k, _l, _m;
        if (!soilNitrogenCanCreate(preview, accepted, dateIssue, canCreate) || !token || creating || previewing)
            return;
        (_k = creationRequest.current) === null || _k === void 0 ? void 0 : _k.abort();
        const controller = new AbortController();
        creationRequest.current = controller;
        setCreating(true);
        setError(null);
        mapCallback.current(null);
        try {
            const response = yield axios.post('/fertilizer/soil-nitrogen/create', {
                field: field.id, season, assessment_date: assessmentDate, planned_application_date: plannedDate,
                source_selection_key: preview.source_selection_key, accept_pilot_assumptions: true,
            }, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } });
            if (controller.signal.aborted)
                return;
            if (((_l = response.data) === null || _l === void 0 ? void 0 : _l.success) !== true || !finiteNumber((_m = response.data) === null || _m === void 0 ? void 0 : _m.id))
                throw new Error('Calculation was not saved');
            setEditing(false);
            setShowSoil(false);
            setPreview(null);
            setAccepted(false);
            setRecordsVersion((value) => value + 1);
        }
        catch (requestError) {
            if (!controller.signal.aborted)
                setError(serverMessage(requestError, 'Не удалось построить карту. Повторите расчёт.'));
        }
        finally {
            if (!controller.signal.aborted)
                setCreating(false);
        }
    });
    return React.createElement(Box, { sx: { p: 2, minWidth: 0,
            '& [aria-label="Легенда карты"] > ul': {
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 270px), 1fr))',
            } } },
        React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", gap: 1, sx: { mb: 1 } },
            React.createElement(Typography, { variant: "subtitle1", component: "h3", sx: { fontWeight: 600, m: 0 } }, "\u0414\u0438\u0444\u0444\u0435\u0440\u0435\u043D\u0446\u0438\u0440\u043E\u0432\u0430\u043D\u043D\u043E\u0435 \u0432\u043D\u0435\u0441\u0435\u043D\u0438\u0435 \u2014 \u0410\u0437\u043E\u0442"),
            React.createElement(Stack, { direction: "row", spacing: 0.5 },
                !formVisible && canCreate && active && React.createElement(Button, { size: "small", onClick: () => {
                        setAssessmentDate(planningContext.today);
                        setPlannedDate(planningContext.today);
                        setPreview(null);
                        setAccepted(false);
                        setEditing(true);
                        setError(null);
                    } }, "\u041F\u0435\u0440\u0435\u0441\u0447\u0438\u0442\u0430\u0442\u044C"),
                React.createElement(Tooltip, { title: "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C \u043F\u0430\u043D\u0435\u043B\u044C" },
                    React.createElement(IconButton, { size: "small", onClick: onCollapse, "aria-label": "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C \u043F\u0430\u043D\u0435\u043B\u044C" },
                        React.createElement(ExpandMoreIcon, null))),
                React.createElement(Tooltip, { title: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" },
                    React.createElement(IconButton, { size: "small", onClick: onClose, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" },
                        React.createElement(CloseIcon, null))))),
        !active ? React.createElement(Alert, { severity: "info" }, planningContext.reason || 'Для архивного сезона карты-задания не рассчитываются. Выберите активный сезон.')
            : React.createElement(React.Fragment, null,
                loadingRecords ? React.createElement(Stack, { direction: "row", gap: 1, alignItems: "center" },
                    React.createElement(CircularProgress, { size: 20 }),
                    React.createElement(Typography, { variant: "body2" }, "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u0440\u0430\u0441\u0447\u0451\u0442\u044B\u2026"))
                    : recordsError ? React.createElement(Alert, { severity: "error", action: React.createElement(Button, { color: "inherit", size: "small", onClick: () => setRecordsVersion((value) => value + 1) }, "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C") }, recordsError)
                        : formVisible ? canCreate ? React.createElement(React.Fragment, null,
                            React.createElement(Typography, { variant: "caption", color: "warning.dark", sx: { display: 'block', mb: 1 } }, PILOT_NOTICE),
                            React.createElement(Stack, { direction: "row", flexWrap: "wrap", gap: 1.5, sx: { mt: 1 } },
                                React.createElement(TextField, { label: "\u0414\u0430\u0442\u0430 \u043E\u0446\u0435\u043D\u043A\u0438", type: "date", size: "small", value: assessmentDate, InputLabelProps: { shrink: true }, inputProps: { min: `${season}-01-01`, max: planningContext.today }, error: Boolean(assessmentIssue), onChange: (event) => changeDate(event.target.value, false) }),
                                React.createElement(TextField, { label: "\u041F\u043B\u0430\u043D\u0438\u0440\u0443\u0435\u043C\u043E\u0435 \u0432\u043D\u0435\u0441\u0435\u043D\u0438\u0435", type: "date", size: "small", value: plannedDate, InputLabelProps: { shrink: true }, inputProps: { min: planningContext.today, max: `${season}-12-31` }, onChange: (event) => changeDate(event.target.value, true) }),
                                React.createElement(Button, { size: "small", variant: "outlined", disabled: Boolean(dateIssue) || previewing || creating || !token, onClick: requestPreview }, previewing ? 'Проверяем анализы…' : 'Проверить анализы')),
                            dateIssue && React.createElement(Typography, { variant: "caption", color: "error", sx: { display: 'block', mt: 0.75 } }, dateIssue),
                            preview && React.createElement(Box, { sx: { mt: 1.5 } },
                                preview.message && React.createElement(Alert, { severity: preview.ready ? 'info' : 'warning', sx: { mb: 1 } }, preview.message),
                                !preview.ready && !preview.message && React.createElement(Alert, { severity: "warning" }, "\u0414\u043B\u044F \u0440\u0430\u0441\u0447\u0451\u0442\u0430 \u043D\u0435\u0434\u043E\u0441\u0442\u0430\u0442\u043E\u0447\u043D\u043E \u043F\u043E\u0434\u0445\u043E\u0434\u044F\u0449\u0438\u0445 \u043F\u043E\u0447\u0432\u0435\u043D\u043D\u044B\u0445 \u0430\u043D\u0430\u043B\u0438\u0437\u043E\u0432 \u0438\u043B\u0438 \u043D\u0435 \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u0438\u0432\u0430\u0435\u0442\u0441\u044F \u043A\u0443\u043B\u044C\u0442\u0443\u0440\u0430 \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u043E\u0433\u043E \u0441\u0435\u0437\u043E\u043D\u0430."),
                                preview.ready && React.createElement(React.Fragment, null,
                                    React.createElement(Typography, { variant: "body2" },
                                        ((_b = preview.profile) === null || _b === void 0 ? void 0 : _b.crop_name) || ((_c = preview.profile) === null || _c === void 0 ? void 0 : _c.crop_label) || 'Культура из выбранного сезона',
                                        " \u00B7 \u0430\u043D\u0430\u043B\u0438\u0437 \u043E\u0442 ",
                                        formatCalendarDate(preview.sample_date),
                                        " \u00B7 \u043F\u0440\u043E\u0431 \u0441 \u0434\u0430\u043D\u043D\u044B\u043C\u0438: ", (_e = (_d = preview.usable_sample_count) !== null && _d !== void 0 ? _d : preview.sample_count) !== null && _e !== void 0 ? _e : '—'),
                                    React.createElement(FormControlLabel, { sx: { mt: 0.5, alignItems: 'flex-start' }, control: React.createElement(Checkbox, { checked: accepted, onChange: (event) => setAccepted(event.target.checked) }), label: React.createElement(Typography, { variant: "body2", sx: { pt: 1 } }, "\u041F\u043E\u043D\u0438\u043C\u0430\u044E, \u0447\u0442\u043E \u0440\u0430\u0441\u0447\u0451\u0442 \u043F\u0440\u0435\u0434\u0432\u0430\u0440\u0438\u0442\u0435\u043B\u044C\u043D\u044B\u0439.") }),
                                    React.createElement(Box, null,
                                        React.createElement(Button, { variant: "contained", size: "small", disabled: !soilNitrogenCanCreate(preview, accepted, dateIssue, canCreate) || creating || previewing, onClick: createCalculation }, creating ? 'Строим карту…' : 'Построить карту')))),
                            current && React.createElement(Button, { size: "small", sx: { mt: 1 }, disabled: creating, onClick: () => {
                                    var _a;
                                    (_a = previewRequest.current) === null || _a === void 0 ? void 0 : _a.abort();
                                    setPreview(null);
                                    setAccepted(false);
                                    setEditing(false);
                                    setError(null);
                                } }, "\u0412\u0435\u0440\u043D\u0443\u0442\u044C\u0441\u044F \u043A \u0441\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u043E\u0439 \u043A\u0430\u0440\u0442\u0435")) : React.createElement(Typography, { variant: "body2", color: "text.secondary" }, "\u0420\u0430\u0441\u0447\u0451\u0442 \u043F\u043E \u0430\u0433\u0440\u043E\u0445\u0438\u043C\u0438\u0447\u0435\u0441\u043A\u043E\u043C\u0443 \u0430\u043D\u0430\u043B\u0438\u0437\u0443 \u0435\u0449\u0451 \u043D\u0435 \u0441\u043E\u0437\u0434\u0430\u043D. \u0415\u0433\u043E \u043C\u043E\u0436\u0435\u0442 \u043F\u043E\u0434\u0433\u043E\u0442\u043E\u0432\u0438\u0442\u044C \u0433\u043B\u0430\u0432\u043D\u044B\u0439 \u0430\u0433\u0440\u043E\u043D\u043E\u043C \u0438\u043B\u0438 \u0430\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440.")
                            : React.createElement(React.Fragment, null,
                                React.createElement(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 0.5 } },
                                    "\u0410\u043D\u0430\u043B\u0438\u0437 \u043E\u0442 ",
                                    formatCalendarDate((_f = visibleRecord === null || visibleRecord === void 0 ? void 0 : visibleRecord.fertilizer_agrochemical_date) !== null && _f !== void 0 ? _f : (_g = metadata === null || metadata === void 0 ? void 0 : metadata.agrochemical) === null || _g === void 0 ? void 0 : _g.date),
                                    " \u00B7 \u0412\u043D\u0435\u0441\u0435\u043D\u0438\u0435 ",
                                    formatCalendarDate(visibleRecord === null || visibleRecord === void 0 ? void 0 : visibleRecord.fertilizer_planned_application_date)),
                                React.createElement(Typography, { variant: "caption", color: "warning.dark", sx: { display: 'block', mb: 1 } }, PILOT_NOTICE),
                                legacySaved && React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', mb: 1 } },
                                    "\u041F\u0440\u0435\u0436\u043D\u044F\u044F \u0432\u0435\u0440\u0441\u0438\u044F \u043A\u0430\u0440\u0442\u044B. ",
                                    canCreate ? 'Обновите через «Пересчитать».' : 'Для обновления обратитесь к главному агроному или администратору.'),
                                React.createElement(FormControlLabel, { control: React.createElement(Switch, { size: "small", checked: showSoil, onChange: (event) => setShowSoil(event.target.checked) }), label: React.createElement(Typography, { variant: "body2" }, "\u0421\u043E\u0434\u0435\u0440\u0436\u0430\u043D\u0438\u0435 \u0430\u0437\u043E\u0442\u0430 \u0432 \u043F\u043E\u0447\u0432\u0435") }),
                                loadingChart ? React.createElement(Stack, { direction: "row", gap: 1, alignItems: "center" },
                                    React.createElement(CircularProgress, { size: 20 }),
                                    React.createElement(Typography, { variant: "body2" }, "\u0417\u0430\u0433\u0440\u0443\u0436\u0430\u0435\u043C \u043A\u0430\u0440\u0442\u0443 \u0438 \u043B\u0435\u0433\u0435\u043D\u0434\u0443\u2026"))
                                    : chartError ? React.createElement(Alert, { severity: "error", action: React.createElement(Button, { color: "inherit", size: "small", onClick: () => setChartVersion((value) => value + 1) }, "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C") }, chartError)
                                        : visibleChart && React.createElement(React.Fragment, null,
                                            React.createElement(FertilizerPanelSummary, { average: visibleChart.average, area: visibleChart.area, summ: visibleChart.summ, nutrient: "nitrogen", soil: showSoil }),
                                            React.createElement(FertilizerLegend, { key: chartKey, compact: true, band: band, items: visibleChart.items, area: visibleChart.area, unit: showSoil ? 'мг/кг' : 'кг N/га', totalLabel: "\u0412\u0441\u0435\u0433\u043E \u0430\u0437\u043E\u0442\u0430", totalUnit: "\u043A\u0433 N" }))),
                error && React.createElement(Alert, { severity: "error", sx: { mt: 1 } }, error)));
}
